import React from "react";
import { Box } from "@mui/material";

/**
 * Interface defining the props for the LayoutBox component.
 * @property {React.ReactNode} children - The child elements to render within the box
 */
interface LayoutBoxProps {
  children: React.ReactNode;
}

/**
 * A reusable layout container component for consistent spacing and alignment.
 * 
 * This component provides a wrapper box with:
 * - Full width layout
 * - Consistent margin spacing
 * - Space-between content alignment
 * - Nested box structure
 * 
 * @component
 * @param {LayoutBoxProps} props - The props passed to the component
 * @param {React.ReactNode} props.children - Child elements
 * 
 * @example
 * ```tsx
 * <LayoutBox>
 *   <Typography>Content Item 1</Typography>
 *   <Typography>Content Item 2</Typography>
 * </LayoutBox>
 * ```
 * 
 * @remarks
 * - Uses Material-UI Box component
 * - Provides 10px margin on all sides
 * - Spans full width of container
 * - Distributes content with space-between
 * 
 * @returns {JSX.Element} A React component that renders a layout container
 */
const LayoutBox = ({ children }: LayoutBoxProps) => {
  return (
    <Box width="100%" m="10px 10px">
      <Box justifyContent="space-between">{children}</Box>
    </Box>
  );
};

export default LayoutBox;
